import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, message, Modal, Select } from "antd";
import { logger } from "../../../../../../../utils";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../application/store";
import I18n from "../../../../../../i18n";
import {
  addNewAddress,
  loadAddresses,
} from "../../../../../../../application/addresses";

const { useForm, Item } = Form;
const { Option } = Select;

const states = [
  "آذربایجان شرقی",
  "آذربایجان غربی",
  "اردبیل",
  "اصفهان",
  "البرز",
  "ایلام",
  "بوشهر",
  "تهران",
  "چهارمحال و بختیاری",
  "خراسان جنوبی",
  "خراسان رضوی",
  "خراسان شمالی",
  "خوزستان",
  "زنجان",
  "سمنان",
  "سیستان و بلوچستان",
  "فارس",
  "قزوین",
  "قم",
  "کردستان",
  "کرمان",
  "کرمانشاه",
  "کهگیلویه و بویراحمد",
  "گلستان",
  "گیلان",
  "لرستان",
  "مازندران",
  "مرکزی",
  "هرمزگان",
  "همدان",
  "یزد",
];

type NewAddressModalProps = {
  visible: boolean;
  onClose: () => void;
};

const NewAddressModal: React.FC<NewAddressModalProps> = ({
  onClose,
  visible,
}) => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { loadingNewAddress } = useAppSelector((state) => state.addresses);
  const [zipCode, setZipCode] = useState<string | undefined>();

  const submit = async () => {
    try {
      await form.validateFields();
      const { state, city, address } = form.getFieldsValue();
      dispatch(addNewAddress(state, city, address, zipCode));
    } catch (error) {
      logger.error(error);
    }
  };

  const stableDispatch = useCallback(dispatch, []);
  const stableOnClose = useCallback(onClose, []);
  useEffect(() => {
    if (loadingNewAddress === "failed") {
      message.error(I18n.general.networkError);
    } else if (loadingNewAddress === "succeeded") {
      stableDispatch(loadAddresses());
      stableOnClose();
    }
  }, [loadingNewAddress, stableDispatch, stableOnClose]);

  const isLoading = loadingNewAddress === "pending";
  return (
    <Modal
      title={I18n.profile.newAddressModalTitle}
      visible={visible}
      onCancel={isLoading ? undefined : onClose}
      onOk={submit}
      closable={!isLoading}
      maskClosable={!isLoading}
      confirmLoading={isLoading}
      width={335}
      bodyStyle={{ paddingBottom: 0 }}
      destroyOnClose
    >
      <Form form={form} fields={[{ name: "zipCode", value: zipCode }]}>
        <Item
          name="state"
          rules={[
            {
              required: true,
              message: I18n.profile.newAddressErrors.emptyState,
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Select placeholder={I18n.profile.state} disabled={isLoading}>
            {states.map((state) => (
              <Option value={state}>{state}</Option>
            ))}
          </Select>
        </Item>
        <Item
          name="city"
          rules={[
            {
              required: true,
              message: I18n.profile.newAddressErrors.emptyCity,
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Input
            maxLength={100}
            placeholder={I18n.profile.city}
            disabled={isLoading}
            allowClear
            required
          />
        </Item>
        <Item
          name="address"
          rules={[
            {
              required: true,
              message: I18n.profile.newAddressErrors.emptyAddress,
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Input.TextArea
            placeholder={I18n.profile.address}
            disabled={isLoading}
            allowClear
            required
          />
        </Item>
        <Item
          name="zipCode"
          rules={[
            {
              len: 10,
              message: I18n.profile.newAddressErrors.shortZipCode,
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Input
            name="zipCode"
            maxLength={10}
            placeholder={I18n.profile.zipCode}
            disabled={isLoading}
            allowClear
            inputMode="numeric"
            onChange={(v) => {
              const value = v.target.value;
              const numberRegex = /^[0-9\b]+$/;
              if (value === "" || numberRegex.test(value)) {
                setZipCode(value);
              }
              form.setFields([
                { name: "zipCode", value: zipCode ? zipCode : "" },
              ]);
            }}
          />
        </Item>
      </Form>
    </Modal>
  );
};

export default NewAddressModal;
